import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
    EstimatedTimeCellVariant,
    EstimatedTimeCellVariantSchema,
} from "@common/types/views/clientsWorkOverviewView";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { resolveColumnVariant } from "@design-system/ViewTable/core/resolveColumnVariant";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ClientsWorkOverviewRow } from "../clientsWorkOverviewViewConfig";
import { EMPTY_CELL_VALUE } from "./emptyCellValue";
import {
    buildWorkEstimateTooltip,
    formatEstimateMinutes,
    type WorkEstimateRow,
} from "./workEstimateTooltip";
import styles from "./WorkEstimatedTimeCell.module.scss";

const SUMMARY_PARTS_BY_VARIANT: Record<
    EstimatedTimeCellVariant,
    (times: { remaining: number; done: number }) => {
        primary: number;
        secondary: number;
    }
> = {
    remainingOfTotal: ({ remaining, done }) => ({
        primary: remaining,
        secondary: remaining + done,
    }),
    completedOfTotal: ({ remaining, done }) => ({
        primary: done,
        secondary: remaining + done,
    }),
    remainingOfCompleted: ({ remaining, done }) => ({
        primary: remaining,
        secondary: done,
    }),
};

/** An application's estimated time in the column's variant, without the breakdown. */
export const WorkEstimatedTimeSummary = ({
    row: { estimatedTimeRemaining, estimatedTimeDone },
    variant,
}: {
    row: Pick<WorkEstimateRow, "estimatedTimeRemaining" | "estimatedTimeDone">;
    variant: EstimatedTimeCellVariant;
}) => {
    const { primary, secondary } = SUMMARY_PARTS_BY_VARIANT[variant]({
        remaining: estimatedTimeRemaining,
        done: estimatedTimeDone,
    });

    return (
        <span className={styles.summary}>
            <span className={styles.primary}>
                {formatEstimateMinutes(primary)}
            </span>
            <span className={styles.separator}> / </span>
            <span className={styles.secondary}>
                {formatEstimateMinutes(secondary)}
            </span>
        </span>
    );
};

/** An application's estimated time in the column's variant, with the per-member breakdown on hover. */
export const WorkEstimatedTime = ({
    row,
    variant,
    title,
}: {
    row: WorkEstimateRow;
    variant: EstimatedTimeCellVariant;
    /** Heads the breakdown, e.g. the field's name where nothing else says it. */
    title?: string;
}) => (
    <Tooltip {...buildWorkEstimateTooltip({ row, title })} side="bottom">
        <span>
            <WorkEstimatedTimeSummary row={row} variant={variant} />
        </span>
    </Tooltip>
);

export const WorkEstimatedTimeCell = memo(
    ({
        row,
        column,
    }: CellContext<ViewTableFeatures, ClientsWorkOverviewRow, unknown>) => {
        if (row.original.rowType !== "work") {
            return EMPTY_CELL_VALUE;
        }

        const { estimatedTimeRemaining, estimatedTimeDone } = row.original;

        if (estimatedTimeRemaining === 0 && estimatedTimeDone === 0) {
            return (
                <ViewTableCell className={styles.cell}>
                    <span className={styles.empty}>-</span>
                </ViewTableCell>
            );
        }

        const variant = resolveColumnVariant({
            meta: column.columnDef.meta,
            schema: EstimatedTimeCellVariantSchema,
            fallback: DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
        });

        return (
            <ViewTableCell className={styles.cell}>
                <WorkEstimatedTime row={row.original} variant={variant} />
            </ViewTableCell>
        );
    },
);

WorkEstimatedTimeCell.displayName = "WorkEstimatedTimeCell";
